import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Monitor } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/stores/authStore";
import { getDeviceId, getDeviceDisplayName } from "@/utils/deviceId";
import trustedDeviceService from "@/services/trustedDeviceService";

export default function DeviceNameDialog() {
  const { showDeviceNamePrompt, dismissDeviceNamePrompt } = useAuthStore();
  const { toast } = useToast();
  const defaultName = getDeviceDisplayName();
  const [deviceName, setDeviceName] = useState(defaultName);

  const updateMutation = useMutation({
    mutationFn: (name: string) => {
      const deviceId = getDeviceId();
      if (!deviceId) throw new Error("기기 ID를 찾을 수 없습니다.");
      return trustedDeviceService.updateName(deviceId, name);
    },
    onSuccess: () => {
      toast({
        title: "기기 등록 완료",
        description: "이 기기가 신뢰할 수 있는 기기로 등록되었습니다.",
      });
      dismissDeviceNamePrompt();
    },
    onError: () => {
      // 이름 변경 실패해도 기기는 이미 등록됨 - 조용히 닫기
      dismissDeviceNamePrompt();
    },
  });

  const handleSave = () => {
    const name = deviceName.trim() || defaultName;
    updateMutation.mutate(name);
  };

  const handleSkip = () => {
    // 기본 이름으로 저장
    updateMutation.mutate(defaultName);
  };

  const handleDismiss = () => {
    // 오버레이 클릭/Escape 시 즉시 닫고, 기본 이름으로 백그라운드 저장
    dismissDeviceNamePrompt();
    const deviceId = getDeviceId();
    if (deviceId) {
      trustedDeviceService.updateName(deviceId, defaultName).catch(() => {});
    }
  };

  return (
    <Dialog open={showDeviceNamePrompt} onOpenChange={handleDismiss}>
      <DialogContent className="sm:w-[calc(100%-2rem)] max-w-md mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            새 기기가 등록되었습니다
          </DialogTitle>
          <DialogDescription>
            이 기기가 신뢰할 수 있는 기기로 자동 등록되었습니다.
            기기를 구분하기 쉽도록 이름을 지정해주세요.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="device-name">기기 이름</Label>
            <Input
              id="device-name"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="예: 사무실 PC, 집 노트북"
              maxLength={100}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              프로필 페이지에서 언제든 변경할 수 있습니다.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={updateMutation.isPending}
          >
            기본 이름 사용
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            저장
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
